import { ChevronRight } from 'lucide-react'
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react'

export interface ListCardField {
    /** Nhãn ngắn, nằm bên trái dòng meta. */
    label: string
    value: ReactNode
    /** Giá trị dài (địa chỉ, ghi chú) — xuống dòng riêng thay vì ép sang phải. */
    full?: boolean
}

export interface ListCardMetric {
    /** Nhãn nhỏ phía trên con số. Bỏ trống nếu con số đã tự rõ nghĩa. */
    label?: string
    value: ReactNode
    /** Tô màu tiền vào/ra. Mặc định dùng màu chữ thường. */
    tone?: 'default' | 'positive' | 'negative'
}

interface ListCardProps {
    /**
     * Dòng nhận diện chính. Caller thường truyền
     * `<Link to=... params=...>{code}</Link>` để giữ type-safe route.
     */
    title: ReactNode
    /** Dòng phụ dưới title: tên khách, hộ trồng, danh mục... */
    subtitle?: ReactNode
    /** Badge trạng thái — caller tự chọn màu/chữ. */
    badge?: ReactNode
    /**
     * Con số người dùng quét mắt tìm đầu tiên (tổng tiền, khối lượng).
     * Tách khỏi `fields` để được cỡ chữ lớn và canh cột số.
     */
    metric?: ListCardMetric
    /** Các cặp nhãn/giá trị phụ. Field có value rỗng sẽ bị bỏ qua. */
    fields?: ListCardField[]
    /** Nút thao tác — nằm ở hàng riêng cuối card, không chen vào header. */
    actions?: ReactNode
    /** Tap cả card → mở chi tiết. */
    onClick?: () => void
}

export const LIST_CARD_TITLE_CLASS =
    'font-mono text-sm font-semibold text-foreground hover:text-primary hover:underline break-words'

const isEmpty = (value: ReactNode) =>
    value === null || value === undefined || value === ''

const METRIC_TONE: Record<NonNullable<ListCardMetric['tone']>, string> = {
    default: 'text-foreground',
    positive: 'text-emerald-600 dark:text-emerald-400',
    negative: 'text-rose-600 dark:text-rose-400',
}

/**
 * Card mobile hiển thị 1 dòng danh sách, thay cho bảng ở viewport < md.
 *
 * Bố cục 4 vùng theo thứ tự mắt đọc: định danh → số liệu chính → meta phụ →
 * thao tác. Mục đích là không lặp lại lỗi "bảng xoay dọc": nếu mọi field đều
 * cùng cỡ chữ thì card cũng khó quét như bảng.
 *
 * Danh sách lô/phiếu kho đã có `LotListCard`/`NoteListCard` với slot cố định
 * theo nghiệp vụ — dùng cái đó, đừng thay bằng cái này.
 */
export function ListCard({
    title,
    subtitle,
    badge,
    metric,
    fields,
    actions,
    onClick,
}: ListCardProps) {
    const clickable = !!onClick
    const stopPropagation = (e: MouseEvent) => e.stopPropagation()
    const onKeyDown = (e: KeyboardEvent) => {
        if (!onClick) return
        if (e.key !== 'Enter' && e.key !== ' ') return
        e.preventDefault()
        onClick()
    }
    const visibleFields = (fields ?? []).filter((f) => !isEmpty(f.value))
    const tone = METRIC_TONE[metric?.tone ?? 'default']

    return (
        <div
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={onClick}
            onKeyDown={clickable ? onKeyDown : undefined}
            className={[
                'rounded-lg border border-border bg-card text-card-foreground',
                'shadow-xs transition-colors duration-200',
                clickable
                    ? 'cursor-pointer hover:border-primary/40 hover:bg-accent/40 active:bg-accent/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                    : '',
            ].join(' ')}
        >
            {/* Vùng 1 — định danh */}
            <div className="flex items-start gap-3 px-4 pt-3.5 pb-3">
                <div className="min-w-0 flex-1">
                    <div onClick={stopPropagation}>{title}</div>
                    {isEmpty(subtitle) ? null : (
                        <div className="mt-1 text-sm text-muted-foreground break-words">
                            {subtitle}
                        </div>
                    )}
                </div>
                {/* Badge là vùng TĨNH: không chặn nổi bọt, để chạm vào góc
                    phải card vẫn mở được chi tiết. Control tương tác (dropdown,
                    nút) phải đặt ở slot `actions`. */}
                <div className="flex shrink-0 items-center gap-1.5">
                    {badge}
                    {clickable ? (
                        <ChevronRight
                            data-testid="list-card-chevron"
                            aria-hidden="true"
                            className="size-4 text-muted-foreground/60"
                        />
                    ) : null}
                </div>
            </div>

            {/* Vùng 2 — số liệu chính */}
            {metric ? (
                <div
                    data-testid="list-card-metric"
                    className="border-t border-border/60 px-4 py-2.5"
                >
                    {isEmpty(metric.label) ? null : (
                        <div className="text-[11px] leading-none text-muted-foreground">
                            {metric.label}
                        </div>
                    )}
                    <div
                        className={`mt-1 text-lg leading-tight font-semibold tabular-nums ${tone}`}
                    >
                        {metric.value}
                    </div>
                </div>
            ) : null}

            {/* Vùng 3 — meta phụ: nhãn trái, giá trị phải, canh cột số */}
            {visibleFields.length > 0 ? (
                <dl className="divide-y divide-border/40 border-t border-border/60 px-4 text-sm">
                    {visibleFields.map((field) =>
                        field.full ? (
                            <div key={field.label} className="py-2">
                                <dt className="text-[11px] text-muted-foreground">
                                    {field.label}
                                </dt>
                                <dd className="mt-0.5 break-words">
                                    {field.value}
                                </dd>
                            </div>
                        ) : (
                            <div
                                key={field.label}
                                className="flex items-center justify-between gap-3 py-2"
                            >
                                <dt className="shrink-0 text-muted-foreground">
                                    {field.label}
                                </dt>
                                <dd className="min-w-0 text-right font-medium tabular-nums break-words">
                                    {field.value}
                                </dd>
                            </div>
                        ),
                    )}
                </dl>
            ) : null}

            {/* Vùng 4 — thao tác */}
            {actions ? (
                <div
                    data-testid="list-card-actions"
                    onClick={stopPropagation}
                    className="flex items-center justify-end gap-2 border-t border-border/60 px-2 py-1.5"
                >
                    {actions}
                </div>
            ) : null}
        </div>
    )
}
